import { useAuth } from '@/hooks/useAuth';
import AuthForm from './AuthForm';

interface Props {
  onBack: () => void;
}

/**
 * Connexion gestionnaire de flotte.
 *
 * Il n'y a pas de compte « gestionnaire » distinct : le compte est le même,
 * seul le rôle porté par le profil change. Après connexion, si le compte n'est
 * pas gestionnaire, on le dit clairement au lieu de laisser l'utilisateur
 * devant une console vide.
 */
export default function FleetManagerLogin({ onBack }: Props) {
  const { loading, isSignedIn, isFleetManager, profile, signOut } = useAuth();

  return (
    <div className="min-h-screen bg-gray-950 flex flex-col">
      <div className="bg-gradient-to-r from-blue-800 to-indigo-700 px-6 pt-14 pb-8">
        <button
          onClick={onBack}
          className="w-9 h-9 flex items-center justify-center rounded-full bg-white/20 mb-5"
          aria-label="Retour"
        >
          <i className="ri-arrow-left-line text-white text-lg"></i>
        </button>
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-2xl bg-white/15 flex items-center justify-center border border-white/20">
            <i className="ri-shield-keyhole-line text-white text-2xl"></i>
          </div>
          <div>
            <h1 className="text-white font-bold text-xl tracking-tight">Gestionnaire de flotte</h1>
            <p className="text-blue-200 text-xs mt-0.5">Console d&apos;administration</p>
          </div>
        </div>
      </div>

      <div className="flex-1 px-5 pt-6 pb-10 flex flex-col items-center">
        {loading && <i className="ri-loader-4-line text-blue-300 text-2xl animate-spin"></i>}

        {!loading && !isSignedIn && <AuthForm allowRegister={false} tone="dark" />}

        {!loading && isSignedIn && !isFleetManager && (
          <div className="bg-white rounded-3xl shadow-2xl p-6 w-full max-w-sm">
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4">
              <i className="ri-information-line text-amber-500 text-sm flex-shrink-0 mt-0.5"></i>
              <p className="text-xs text-amber-700 font-medium leading-snug">
                Ce compte est un compte conducteur. Pour gérer une flotte, créez
                votre société depuis l&apos;onglet Profil — vous en deviendrez
                automatiquement le gestionnaire.
              </p>
            </div>
            <p className="text-gray-400 text-xs mb-4">
              Connecté en tant que <span className="font-semibold text-gray-600">{profile?.email}</span>
            </p>
            <button
              onClick={() => void signOut()}
              className="w-full py-3.5 rounded-2xl bg-gray-100 text-gray-600 font-bold text-sm flex items-center justify-center gap-2"
            >
              <i className="ri-logout-box-line text-sm"></i>
              Changer de compte
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
